import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.select(`
          *,
          user:user_id (
            full_name,
            email,
            role
          )
        `)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    const severityConfig = {
      info: { bg: 'bg-secondary-100', text: 'text-secondary-700', label: 'Info', icon: 'Info' },
      warning: { bg: 'bg-warning-100', text: 'text-warning-700', label: 'Warning', icon: 'AlertTriangle' },
      error: { bg: 'bg-error-100', text: 'text-error-700', label: 'Error', icon: 'AlertCircle' },
      critical: { bg: 'bg-error-100', text: 'text-error-700', label: 'Critical', icon: 'AlertOctagon' }
    };
    const config = severityConfig?.[severity] || severityConfig?.info;
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const getActionIcon = (action) => {
    if (action?.includes('login')) return 'LogIn';
    if (action?.includes('logout')) return 'LogOut';
    if (action?.includes('create')) return 'Plus';
    if (action?.includes('update')) return 'Edit';
    if (action?.includes('delete')) return 'Trash';
    if (action?.includes('approve')) return 'Check';
    if (action?.includes('reject')) return 'X';
    if (action?.includes('suspend')) return 'Ban';
    return 'Activity';
  };

  const filteredLogs = logs?.filter((log) => {
    const matchesSeverity = filterSeverity === 'all' || log?.severity === filterSeverity;
    const matchesAction = filterAction === 'all' || log?.action?.includes(filterAction);
    const matchesSearch = 
      log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.resource_type?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSeverity && matchesAction && matchesSearch;
  });

  if (loading) {
    return (
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary-100 rounded w-1/4"></div>
          <div className="h-64 bg-secondary-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-card">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-heading font-bold text-text-primary">Security Audit Logs</h2>
            <p className="text-sm text-text-secondary mt-1">
              System activity tracking and security monitoring
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth">
              <Icon name="Download" size={16} />
              <span>Export Logs</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e?.target?.value)}
            className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Severities</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
            <option value="critical">Critical</option>
          </select>
          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e?.target?.value)}
            className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Actions</option>
            <option value="login">Login/Logout</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
            <option value="approve">Approve/Reject</option>
          </select>
        </div>
      </div>

      {/* Logs List */}
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {filteredLogs?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="FileText" size={48} className="mx-auto text-text-secondary mb-4" />
            <p className="text-text-secondary">No audit logs found</p>
          </div>
        ) : (
          filteredLogs?.map((log) => (
            <div key={log?.id} className="p-4 hover:bg-secondary-50 transition-smooth">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <Icon name={getActionIcon(log?.action)} size={20} className="text-secondary-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-text-primary">{log?.action?.replace(/_/g, ' ')}</span>
                    {getSeverityBadge(log?.severity)}
                  </div>
                  <div className="space-y-1 text-sm text-text-secondary">
                    {log?.user && (
                      <div className="flex items-center space-x-2">
                        <Icon name="User" size={12} />
                        <span>{log?.user?.full_name} ({log?.user?.email})</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Icon name="Package" size={12} />
                      <span>Resource: {log?.resource_type}</span>
                      {log?.resource_id && <span className="text-xs text-text-secondary">ID: {log?.resource_id?.slice(0, 8)}...</span>}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={12} />
                      <span>{format(new Date(log?.created_at), 'MMM dd, yyyy HH:mm:ss')}</span>
                    </div>
                    {log?.ip_address && (
                      <div className="flex items-center space-x-2">
                        <Icon name="Globe" size={12} />
                        <span>IP: {log?.ip_address}</span>
                      </div>
                    )}
                    {log?.details && Object.keys(log?.details)?.length > 0 && (
                      <div className="mt-2 p-2 bg-secondary-50 rounded text-xs font-mono">
                        {JSON.stringify(log?.details, null, 2)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AuditLogs;